import classNames from "classnames";
import React, { useState } from "react";
import KuikaButton from "../kuika-button/kuika-button";
import KuikaPopover from "../kuika-popover/kuika-popover";
import KuikaSmallText from "../typography/SmallText/SmallText";
import KuikaTextMedium from "../typography/TextMedium/TextMedium";
import "./kuika-popconfirm.scss";

export type KuikaPopconfirmTone = "default" | "danger";

type KuikaPopconfirmPosition = "top" | "right" | "bottom" | "left";
type KuikaPopconfirmAlign = "start" | "center" | "end";

export interface IKuikaPopconfirmProps {
  /** The trigger. Anything the popover can anchor to, including a ref-forwarding
   *  component such as `KuikaTooltip`. */
  children: React.ReactNode;
  /** The question itself. It lives in the popover's CONTENT, never its `title` slot: that slot is
   *  uppercased with a rule under it — a section caption, not a sentence someone answers. */
  title: React.ReactNode;
  /** Optional second line under the question — the consequence, not a restatement of it. */
  description?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** `danger` turns the confirm button red. It colours only the commit — the question and the
   *  cancel stay neutral, so the destructive choice is the one thing that stands out. */
  tone?: KuikaPopconfirmTone;
  /** Controlled open state. Omit it and the popconfirm keeps its own. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onConfirm: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /** Called for every way out that is not a confirm: the cancel button (with its event), and a
   *  dismissal — Escape, a press outside, or the trigger being clicked again (with none). */
  onCancel?: (event?: React.MouseEvent<HTMLButtonElement>) => void;
  position?: KuikaPopconfirmPosition;
  align?: KuikaPopconfirmAlign;
  confirmTestId?: string;
  cancelTestId?: string;
  className?: string;
}

const KuikaPopconfirm = ({
  children,
  title,
  description,
  confirmLabel = "Yes",
  cancelLabel = "No",
  tone = "default",
  open: controlledOpen,
  onOpenChange,
  onConfirm,
  onCancel,
  position = "top",
  align = "center",
  confirmTestId,
  cancelTestId,
  className
}: IKuikaPopconfirmProps): JSX.Element => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState<boolean>(false);
  const isControlled: boolean = controlledOpen !== undefined;
  const isOpen: boolean = controlledOpen ?? uncontrolledOpen;

  const setOpen = (nextOpen: boolean): void => {
    if (!isControlled) {
      setUncontrolledOpen(nextOpen);
    }
    onOpenChange?.(nextOpen);
  };

  /**
   * Radix only calls back here when RADIX decided to change the state — Escape, a press outside,
   * or the trigger toggling. A confirm or cancel button closes by moving our own state, which
   * never reaches this handler, so no "was that a real dismissal?" flag is needed to keep
   * `onCancel` from firing after a confirm.
   */
  const handleVisibleChange = (nextVisible: boolean): void => {
    if (!nextVisible) {
      onCancel?.();
    }
    setOpen(nextVisible);
  };

  const handleConfirm = (event: React.MouseEvent<HTMLButtonElement>): void => {
    onConfirm(event);
    setOpen(false);
  };

  const handleCancel = (event: React.MouseEvent<HTMLButtonElement>): void => {
    onCancel?.(event);
    setOpen(false);
  };

  /**
   * The surface is portalled to `document.body`, but React still bubbles its events up the REACT
   * tree — so a confirm sitting on a clickable row would run the row's own handler on its way out.
   * The surface swallows the click before it can.
   */
  const handleSurfaceClick = (event: React.MouseEvent): void => {
    event.stopPropagation();
  };

  return (
    <KuikaPopover
      visible={isOpen}
      onVisibleChange={handleVisibleChange}
      position={position}
      align={align}
      role="alertdialog"
      onClick={handleSurfaceClick}
      content={
        <div className={classNames("kuika-popconfirm", className)}>
          <div className="kuika-popconfirm__question">
            <KuikaTextMedium className="kuika-popconfirm__title">{title}</KuikaTextMedium>
            {description && <KuikaSmallText className="kuika-popconfirm__description">{description}</KuikaSmallText>}
          </div>
          <div className="kuika-popconfirm__actions kuika-flex-end--gap-s">
            <KuikaButton size="sm" type="text" color="neutral" onClick={handleCancel} data-testid={cancelTestId}>
              {cancelLabel}
            </KuikaButton>
            <KuikaButton
              size="sm"
              type="primary"
              color={tone === "danger" ? "danger" : "default"}
              onClick={handleConfirm}
              data-testid={confirmTestId}
            >
              {confirmLabel}
            </KuikaButton>
          </div>
        </div>
      }
    >
      {children}
    </KuikaPopover>
  );
};

export default KuikaPopconfirm;
